import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  trigger: React.ReactNode;
  /** Either a plain node, or a render function that receives a `close` callback (handy for menu items that should dismiss the popover once clicked). */
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  offset?: number;
  openOnHover?: boolean;
}

const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  offset = 8,
  openOnHover = false,
}) => {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const [placement, setPlacement] = useState<
    "top" | "bottom" | "left" | "right"
  >("bottom");

  // measure and position only after mount
  useEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }

    const updatePosition = () => {
      if (!triggerRef.current || !popoverRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const popRect = popoverRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      let top = 0;
      let left = 0;
      let newPlacement: typeof placement = "bottom";

      if (rect.bottom + popRect.height + offset <= vh) {
        top = rect.bottom + offset;
        newPlacement = "bottom";
      } else if (rect.top - popRect.height - offset >= 0) {
        top = rect.top - popRect.height - offset;
        newPlacement = "top";
      } else if (rect.right + popRect.width + offset <= vw) {
        top = rect.top;
        left = rect.right + offset;
        newPlacement = "right";
      } else {
        top = rect.top;
        left = rect.left - popRect.width - offset;
        newPlacement = "left";
      }

      if (newPlacement === "top" || newPlacement === "bottom") {
        left = Math.min(Math.max(8, rect.left), vw - popRect.width - 8);
      } else {
        top = Math.min(
          Math.max(8, rect.top + rect.height / 2 - popRect.height / 2),
          vh - popRect.height - 8
        );
      }

      setCoords({ top, left });
      setPlacement(newPlacement);
      setReady(true); // ✅ show only once positioned
    };

    const timeout = setTimeout(updatePosition, 0);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  // close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node) &&
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const close = () => setOpen(false);
  const renderedChildren =
    typeof children === "function" ? children(close) : children;

  return (
    <div className="relative inline-block">
      <div
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onMouseEnter={() => openOnHover && setOpen(true)}
        onMouseLeave={() => openOnHover && setOpen(false)}
      >
        {trigger}
      </div>

      {open &&
        ready &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-40 rounded-md border bg-white shadow-lg transition-all duration-150 ease-out opacity-0 scale-95 animate-[popoverShow_0.15s_ease-out_forwards] dark:bg-gray-800 dark:border-gray-700"
            onMouseEnter={() => openOnHover && setOpen(true)}
            onMouseLeave={() => openOnHover && setOpen(false)}
            style={{
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
              width: "fit-content",
              transformOrigin:
                placement === "top"
                  ? "bottom left"
                  : placement === "bottom"
                  ? "top left"
                  : placement === "left"
                  ? "right center"
                  : "left center",
            }}
          >
            {renderedChildren}
          </div>,
          document.body
        )}
      {/* Invisible placeholder for measuring position before showing */}
      {open &&
        !ready &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed opacity-0 pointer-events-none"
            style={{ top: -9999, left: -9999 }}
          >
            {renderedChildren}
          </div>,
          document.body
        )}
    </div>
  );
};

export default Popover;
